export type NumberForgeStrategy = 'tens-and-ones' | 'place-value-composition';
export type NumberForgeRegrouping = 'none' | 'available' | 'required';
export type NumberForgePlace = 'hundreds' | 'tens' | 'ones';

export const NUMBER_FORGE_PLACE_VALUES: Readonly<Record<NumberForgePlace, number>> = {
  hundreds: 100,
  tens: 10,
  ones: 1,
};

export interface NumberForgeConfig {
  readonly id: string;
  readonly skillId: string;
  readonly addendA: number;
  readonly addendB: number;
  readonly strategy: NumberForgeStrategy;
  readonly target: number;
  readonly activePlaces: readonly NumberForgePlace[];
  readonly regrouping: NumberForgeRegrouping;
  readonly completionWorldEvent: string;
}

export type NumberForgePlaceCounts = Readonly<Record<NumberForgePlace, number>>;

const PLACE_ORDER: readonly NumberForgePlace[] = ['hundreds', 'tens', 'ones'];

export function decomposeNumberForgeValue(value: number): NumberForgePlaceCounts {
  if (!Number.isInteger(value) || value < 0 || value > 999) {
    throw new RangeError('Number Forge values must be whole numbers from 0 through 999.');
  }

  const hundreds = Math.floor(value / 100);
  const tens = Math.floor((value % 100) / 10);
  const ones = value % 10;
  return { hundreds, tens, ones };
}

export function validateNumberForgeConfig(config: NumberForgeConfig) {
  const values = [config.addendA, config.addendB, config.target];
  if (values.some(value => !Number.isInteger(value) || value < 0 || value > 999)) {
    throw new RangeError('Number Forge values must be whole numbers from 0 through 999.');
  }
  if (config.addendA + config.addendB !== config.target) {
    throw new RangeError('Number Forge target must equal addendA + addendB.');
  }
  if (!config.id.trim() || !config.skillId.trim() || !config.completionWorldEvent.trim()) {
    throw new RangeError('Number Forge identifiers must not be empty.');
  }

  const uniquePlaces = new Set(config.activePlaces);
  if (uniquePlaces.size !== config.activePlaces.length || config.activePlaces.length === 0) {
    throw new RangeError('Number Forge activePlaces must contain distinct place values.');
  }

  const requiredPlaces = PLACE_ORDER.filter(place => (
    decomposeNumberForgeValue(config.addendA)[place] > 0
    || decomposeNumberForgeValue(config.addendB)[place] > 0
    || decomposeNumberForgeValue(config.target)[place] > 0
  ));
  if (requiredPlaces.some(place => !uniquePlaces.has(place))) {
    throw new RangeError('Number Forge activePlaces must represent every non-zero place in the challenge.');
  }

  const a = decomposeNumberForgeValue(config.addendA);
  const b = decomposeNumberForgeValue(config.addendB);
  const needsRegrouping = a.ones + b.ones >= 10 || a.tens + b.tens >= 10;
  if (config.regrouping === 'none' && needsRegrouping) {
    throw new RangeError('A no-regrouping Number Forge challenge cannot require a place-value exchange.');
  }
  if (config.regrouping === 'required' && !needsRegrouping) {
    throw new RangeError('A required-regrouping Number Forge challenge must include a place-value exchange.');
  }

  return config;
}

export const ADDITION_CITADEL_NUMBER_FORGE_1 = validateNumberForgeConfig({
  id: 'addition-citadel-number-forge-1',
  skillId: 'math-addition-two-digit-tens-and-ones',
  addendA: 34,
  addendB: 25,
  strategy: 'tens-and-ones',
  target: 59,
  activePlaces: ['tens', 'ones'],
  regrouping: 'none',
  completionWorldEvent: 'forge-powered-1',
});
